import React, { FC, PropsWithChildren, Suspense, useCallback, useState } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { BackdropWithSpinner, ScrollToTop, SpinLoader } from "@/components/atoms";
import mobileIntegration from "@/integration";
import { cancelRequests } from "@/shared/apis/apiRoot";
import { UrlManager } from "@/shared/apis/UrlManager";
import { ViewMode } from "@/shared/contexts";
import { IFilterItem } from "@/shared/contexts/localFilterContext";
import { useAuth, useGlobalFilter, useNavigation, useViewMode } from "@/shared/hooks";

import {
    StyledAuthPageBody,
    StyledAuthPageContainer,
    StyledMainHeader,
    StyledPageContainer,
    StyledSubHeader,
} from "../styled";
import AuthenticatedBrokerHeader from "./components/AuthenticatedBrokerHeader/AuthenticatedBrokerHeader";
import AuthenticatedHeader from "./components/AuthenticatedHeader/AuthenticatedHeader";
import AuthenticatedSubHeader from "./components/AuthenticatedSubHeader/AuthenticatedSubHeader";

type StandardLayoutProps = {
    title?: string;
    localFilters?: IFilterItem[];
    className?: string;
    disableSearch?: boolean;
    showBrokerInfos?: boolean;
    showGlobalFilter?: boolean;
    showEnvironmentSwitch?: boolean;
    hideHeader?: boolean;
    hideSubHeader?: boolean;
    displayLinkedDocumentsButton?: boolean;
    onOpenSearch?: () => void;
    onOpenLinkedDocuments?: () => void;
};

const StandardLayout: FC<PropsWithChildren<StandardLayoutProps>> = ({
    title,
    children,
    localFilters,
    className,
    disableSearch = false,
    showBrokerInfos = false,
    showGlobalFilter = false,
    showEnvironmentSwitch = false,
    hideHeader = false,
    hideSubHeader = false,
    displayLinkedDocumentsButton = false,
    onOpenSearch,
    onOpenLinkedDocuments,
}) => {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { viewMode } = useViewMode();
    const [isLoginPending, setLoginPending] = useState(false);
    const { navToUrl, navToRoot } = useNavigation();
    const { user, changeBroker } = useAuth();
    const { isProEnvironment } = useGlobalFilter();
    const { i18n } = useTranslation();
    const handleBrokerSelect = async (broker: MyBrokerApi.Broker, availableBrokers: MyBrokerApi.Broker[]) => {
        setLoginPending(true);

        await queryClient.cancelQueries();
        cancelRequests();

        await changeBroker(broker.officeId);
        setLoginPending(false);
        queryClient.setQueryData("availableBrokers", availableBrokers);
        navToRoot();
    };

    // The Helmet component has an issue that doesn't render if consider that it was already rendered
    // So we re-render the Helmet component when any of these props change
    // This avoids not passing this props when language changes (and keep broker color as default)

    const AuthenticatedHelmet = useCallback(() => {
        return (
            <Helmet>
                {title && <title>{`MyBroker - ${title}`}</title>}
                <html lang={i18n.language} />
                <meta name="robots" content="noindex" />
                <body
                    data-custom-theme={user.currentBrokerId}
                    data-theme-variant={isProEnvironment ? "pro" : "private"}
                />
                {!mobileIntegration.isNativeApp() && user.currentBrokerId && (
                    <link rel="stylesheet" href={UrlManager.brokerSchema(user.currentBrokerId)} />
                )}
            </Helmet>
        );
    }, [i18n.language, isProEnvironment, user.currentBrokerId, title]);

    const handleRedirect = (url?: string) => {
        navToUrl(url);
    };

    return (
        <StyledAuthPageContainer className={className}>
            <AuthenticatedHelmet />
            {isLoginPending && <BackdropWithSpinner />}
            {!hideHeader && viewMode === ViewMode.FULL && (
                <>
                    <StyledMainHeader>
                        <AuthenticatedHeader
                            onBrokerSelect={handleBrokerSelect}
                            onRedirectTo={handleRedirect}
                            disableSearch={disableSearch}
                        />
                    </StyledMainHeader>
                    {!isMobileScreen && showBrokerInfos && (
                        <Suspense fallback={null}>
                            <ErrorBoundary fallback={<></> /* hide when failed */}>
                                <AuthenticatedBrokerHeader />
                            </ErrorBoundary>
                        </Suspense>
                    )}
                    {!hideSubHeader && (
                        <StyledSubHeader $isDesktop={!isMobileScreen}>
                            <Suspense fallback={null}>
                                <ErrorBoundary fallback={<></> /* hide when failed */}>
                                    <AuthenticatedSubHeader
                                        onOpenSearch={onOpenSearch}
                                        onOpenLinkedDocuments={onOpenLinkedDocuments}
                                        localFilters={localFilters}
                                        showGlobalFilter={showGlobalFilter}
                                        showEnvironmentSwitch={showEnvironmentSwitch}
                                        displayLinkedDocumentsButton={displayLinkedDocumentsButton}
                                    />
                                </ErrorBoundary>
                            </Suspense>
                        </StyledSubHeader>
                    )}
                </>
            )}
            <StyledAuthPageBody className="authBody">
                <ScrollToTop />
                <StyledPageContainer>
                    <Suspense fallback={<SpinLoader />}>{children}</Suspense>
                </StyledPageContainer>
            </StyledAuthPageBody>
        </StyledAuthPageContainer>
    );
};

export default StandardLayout;
